import { useEffect, useRef, useState } from "react";

/**
 * One time IntersectionObserver visibility hook.
 * Adds visibility once `threshold` of the element is in view, then unobserves.
 */
export function useInViewOnce(threshold = 0.2) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

/**
 * Restrained one time section reveal: opacity 0 / translateY(20px) to
 * opacity 1 / translateY(0) over 550ms. Styles live in index.css (.reveal).
 * `delay` is in milliseconds and is used to stagger related siblings.
 */
export function Reveal({ children, delay = 0, className = "", as: Tag = "div", ...rest }) {
  const [ref, visible] = useInViewOnce(0.2);
  return (
    <Tag
      ref={ref}
      className={`reveal${visible ? " is-visible" : ""} ${className}`.trim()}
      style={delay ? { "--reveal-delay": `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}

export default Reveal;
